import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Spin, Table, Space, Popconfirm, Tag, Progress, Modal, Select, Alert, Collapse } from 'antd';
import { ArrowLeftOutlined, ExperimentOutlined, EyeOutlined, DeleteOutlined, SyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createTask, getTasks, deleteTask, cancelTask, TaskResponse } from '@/utils/taskManager';
import { isProfileVerifyRequired } from '@/api/taskManagerApi';
import { getVisitorId } from '@/utils/session';
import CLBTopeResult from '@/components/results/antibody/CLBTopeResult';
import { formatUTCToLocal } from '@/utils/timeUtils';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

// FASTA解析函数
const parseFasta = (fastaContent: string) => {
  const sequences = [];
  const content = fastaContent.trim();
  
  // 如果没有>符号，则认为是纯序列
  if (!content.startsWith('>')) {
    const cleanSequence = content.replace(/\s+/g, '');
    if (cleanSequence) {
      sequences.push({
        name: 'Protein_1',
        sequence: cleanSequence
      });
    }
    return sequences;
  }
  
  // 解析FASTA格式
  const lines = content.split('\n');
  let currentName = null;
  let currentSequence = [];
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('>')) {
      // 保存前一个序列
      if (currentName !== null && currentSequence.length > 0) {
        sequences.push({
          name: currentName,
          sequence: currentSequence.join('')
        });
      }
      // 开始新序列
      currentName = trimmed.slice(1).trim() || `Protein_${sequences.length + 1}`;
      currentSequence = [];
    } else if (trimmed && currentName !== null) {
      currentSequence.push(trimmed.replace(/\s+/g, ''));
    }
  }
  
  // 保存最后一个序列
  if (currentName !== null && currentSequence.length > 0) {
    sequences.push({
      name: currentName,
      sequence: currentSequence.join('')
    });
  }
  
  return sequences;
};

// 自动检测输入模式
const detectInputMode = (input: string): 'single' | 'fasta' => {
  if (!input) return 'single';
  const trimmed = input.trim();
  // 如果有多个>符号，或者有>符号但不在开头，认为是FASTA格式
  const fastaHeaders = (trimmed.match(/>/g) || []).length;
  if (fastaHeaders > 1 || (fastaHeaders === 1 && trimmed.includes('\n'))) {
    return 'fasta';
  }
  return 'single';
};

export default function AntibodyEpitopePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [resultModalVisible, setResultModalVisible] = useState(false);

  // 加载任务列表
  const loadTasks = async () => {
    setLoading(true);
    try {
      const taskList = await getTasks(0, 100, undefined, 'antibody_service');
      // 注意：匿名提交时，后端会将 user_id 归属到默认管理员用户，无法用 visitorId 做等值筛选
      const filteredTasks = taskList.filter((task: TaskResponse) => 
        task.task_type === 'antibody_epitope'
      );
      
      // 过滤掉已删除的任务
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const finalTasks = filteredTasks.filter(task => !deletedTaskIds.includes(task.id));
      
      setTasks(finalTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch (error) {
      console.error('加载任务列表失败:', error);
      if (!isProfileVerifyRequired(error)) {
        message.error('加载任务列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 提交预测任务
  const handleSubmit = async (values: any) => {
    if (!values.sequences || !values.sequences.trim()) {
      message.error('请输入蛋白质序列');
      return;
    }

    setSubmitting(true);
    try {
      // 解析序列
      const parsedSequences = parseFasta(values.sequences);
      
      if (parsedSequences.length === 0) {
        message.error('未找到有效的蛋白质序列');
        return;
      }
      
      // 自动检测输入模式
      const inputMode = detectInputMode(values.sequences);
      const modeText = inputMode === 'fasta' ? 'FASTA多序列' : '单序列';
      
      // 为每个序列创建一个任务
      const taskPromises = parsedSequences.map((seq, index) => {
        const taskData = {
          name: `表位预测 - ${seq.name}`,
          description: `B细胞表位预测 - ${seq.name} (${modeText})`,
          service_name: 'antibody_service',
          task_type: 'antibody_epitope',
          handler_name: 'epitope_predict_json',
          parameters: {
            sequence: seq.sequence,
            name: seq.name,
            prediction_mode: values.prediction_mode || 'scan',
            threshold: values.threshold || 0.5,
            rolling_window_size: values.rolling_window_size || 15,
            display_mode: values.display_mode || 1
          },
          priority: 1,
          timeout: 300,
          max_retries: 2
        };
        return createTask(taskData);
      });

      await Promise.all(taskPromises);
      message.success(`已提交${parsedSequences.length}个表位预测任务 (${modeText})`);
      form.resetFields();
      loadTasks();
    } catch (error: any) {
      // 更详细的错误信息输出，便于定位 400 Bad Request 的具体原因
      const detail = error?.response?.data?.detail || error?.message || '提交任务失败';
      console.error('提交任务失败:', error);
      message.error(`提交任务失败: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 删除任务 - 修改为本地删除
  const handleDelete = async (taskId: string) => {
    try {
      // 从localStorage中删除任务ID
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      
      // 从当前显示的任务列表中移除
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      
      message.success('任务已删除');
    } catch (error) {
      console.error('删除任务失败:', error);
      message.error('删除任务失败');
    }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务已取消');
      loadTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  // 查看结果详情
  const handleViewResult = (task: TaskResponse) => {
    if (task.result) {
      setSelectedResult({
        title: `${task.name} - 预测结果`,
        data: task.result,
        taskId: task.id
      });
      setResultModalVisible(true);
    } else {
      message.warning('暂无结果数据');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'pending': { color: 'blue', text: '等待中' },
      'running': { color: 'orange', text: '运行中' },
      'completed': { color: 'green', text: '已完成' },
      'failed': { color: 'red', text: '失败' },
      'cancelled': { color: 'gray', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: TaskResponse) => {
        if (record.status === 'completed') return <Progress percent={100} size="small" />;
        if (record.status === 'failed' || record.status === 'cancelled') return <Progress percent={0} status="exception" size="small" />;
        return <Progress percent={progress || 0} size="small" />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => formatUTCToLocal(time),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="middle">
          {record.status === 'completed' && record.result && (
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewResult(record)}
            >
              查看结果
            </Button>
          )}
          {(record.status === 'pending' || record.status === 'running') && (
            <Button 
              type="link" 
              onClick={() => handleCancel(record.id)}
            >
              取消
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个任务吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  const getInputModeHint = (input: string) => {
    if (!input || !input.trim()) return '';
    const mode = detectInputMode(input);
    const sequenceCount = parseFasta(input).length;
    return mode === 'fasta' 
      ? `已识别为FASTA格式，包含${sequenceCount}条序列` 
      : '已识别为单序列输入';
  };

  return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/antibody')}
            style={{ marginBottom: 16 }}
          >
            返回抗体分析
          </Button>
          
          <Typography>
            <Title level={2}>B细胞表位预测 (CLBTope)</Title>
            <Paragraph>
              基于 Random Forest + BLAST 混合算法预测线性和构象B细胞表位。
              自动识别单序列或FASTA格式多序列输入，每条序列将作为独立任务进行处理。
            </Paragraph>
          </Typography>

          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
            message="三种预测模式说明"
            description={
              <Collapse ghost>
                <Collapse.Panel header="点击查看详细说明" key="1">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Tag color="blue">Scan - 扫描模式</Tag>
                      <Paragraph style={{ marginBottom: 8, marginLeft: 8 }}>
                        <strong>用途：</strong>扫描长蛋白质序列，识别其中的B细胞表位区域。<br/>
                        <strong>输入：</strong>完整的蛋白质序列（通常 &gt; 50 个氨基酸）。<br/>
                        <strong>输出：</strong>滑动窗口扫描结果，包含每个窗口肽段的分数和位置信息，自动合并连续表位区域。<br/>
                        <strong>推荐场景：</strong>分析完整抗体或抗原蛋白，寻找潜在的表位区域。
                      </Paragraph>
                    </div>
                    <div>
                      <Tag color="green">Predict - 预测模式</Tag>
                      <Paragraph style={{ marginBottom: 8, marginLeft: 8 }}>
                        <strong>用途：</strong>批量预测多个肽段是否为B细胞表位（虚拟筛选）。<br/>
                        <strong>输入：</strong>多个短肽段序列（每个 8-30 个氨基酸），FASTA格式。<br/>
                        <strong>输出：</strong>每个肽段的预测分数和表位/非表位判定。<br/>
                        <strong>推荐场景：</strong>从肽段库中筛选潜在的B细胞表位候选。
                      </Paragraph>
                    </div>
                    <div>
                      <Tag color="orange">Design - 设计模式</Tag>
                      <Paragraph style={{ marginBottom: 0, marginLeft: 8 }}>
                        <strong>用途：</strong>设计突变体以优化表位活性。<br/>
                        <strong>输入：</strong>单个肽段序列（通常 8-20 个氨基酸）。<br/>
                        <strong>输出：</strong>所有单点突变体的预测分数，帮助识别能增强表位活性的突变位点。<br/>
                        <strong>推荐场景：</strong>优化已知的表位肽段，提高其免疫原性。
                      </Paragraph>
                    </div>
                  </Space>
                </Collapse.Panel>
              </Collapse>
            }
          />
        </div>

        <Card>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="sequences"
              label="蛋白质序列"
              rules={[{ required: true, message: '请输入蛋白质序列' }]}
              extra={
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const sequences = getFieldValue('sequences');
                    const hint = getInputModeHint(sequences);
                    return hint ? <span style={{ color: '#1890ff' }}>{hint}</span> : null;
                  }}
                </Form.Item>
              }
            >
              <TextArea 
                rows={8} 
                placeholder={`支持单序列或FASTA格式输入：

单序列输入：
EVQLVESGGGLVQPGGSLRLSCAASGFTFS...

FASTA格式输入：
>Protein_1
EVQLVESGGGLVQPGGSLRLSCAASGFTFS...
>Protein_2
DIQMTQSPSSLSASVGDRVTITC...`}
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Form.Item
              name="prediction_mode"
              label="预测模式"
              initialValue="scan"
              style={{ minWidth: '200px' }}
              tooltip="scan: 扫描长序列中的表位（推荐） | predict: 整体序列预测 | design: 设计突变优化表位"
            >
              <Select
                options={[
                  { value: 'scan', label: 'Scan - 扫描模式 (推荐)' },
                  { value: 'predict', label: 'Predict - 预测模式' },
                  { value: 'design', label: 'Design - 设计模式' }
                ]}
              />
            </Form.Item>

            <Form.Item
              name="threshold"
              label="预测阈值"
              tooltip="分数阈值。推荐: 0.5 (平衡，~78%表位), 0.6 (保守，~55%表位), 0.7 (严格，~25%表位)"
              initialValue={0.5}
              style={{ minWidth: '150px' }}
            >
              <Input type="number" min={0} max={1} step={0.01} />
            </Form.Item>

            <Form.Item
              name="rolling_window_size"
              label="窗口大小"
              initialValue={15}
              style={{ minWidth: '150px' }}
              tooltip="扫描窗口大小（8-30，仅Scan模式），默认15"
            >
              <Input type="number" min={8} max={30} />
            </Form.Item>

            <Form.Item
              name="display_mode"
              label="显示模式"
              initialValue={1}
              style={{ minWidth: '200px' }}
              tooltip="1: 仅显示B细胞表位 | 2: 显示所有肽段"
            >
              <Select
                options={[
                  { value: 1, label: '仅表位' },
                  { value: 2, label: '所有肽段' }
                ]}
              />
            </Form.Item>
            </div>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
                icon={<ExperimentOutlined />}
                size="large"
              >
                {submitting ? '提交中...' : '预测B细胞表位'}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card 
          title="任务列表" 
          style={{ marginTop: 24 }}
          extra={
            <Button 
              size="small" 
              onClick={loadTasks}
              loading={loading}
              icon={<SyncOutlined />}
            >
              刷新任务
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个任务`,
            }}
          />
        </Card>

        {/* 结果查看模态框 */}
        <Modal
          title={selectedResult?.title}
          open={resultModalVisible}
          onCancel={() => {
            setResultModalVisible(false);
            setSelectedResult(null);
          }}
          width={1200}
          footer={null}
        >
          {selectedResult && (
            <CLBTopeResult
              data={selectedResult.data}
            />
          )}
        </Modal>
      </div>
  );
}